import { useForm, router } from '@inertiajs/react'
import { useRef, type FormEvent } from 'react'
import AccountLayout from '@/layouts/AccountLayout'
import { useAccountPath } from '@/lib/useAccountPath'

interface ApiKey {
  id: number
  name: string
  scope: 'aggregate' | 'admin'
  masked_token: string
  rate_limit_per_minute: number
  revoked_at: string | null
  last_used_at: string | null
  created_at: string
  created_by_email_address: string | null
}

interface Props {
  api_keys: ApiKey[]
  flash_token: string | null
  can_create: boolean
}

export default function ApiKeys({ api_keys, flash_token, can_create }: Props) {
  const ap = useAccountPath()
  const newDialog = useRef<HTMLDialogElement>(null)

  function handleRevoke(key: ApiKey) {
    if (!confirm(`Revoke "${key.name}"? This can't be undone. Any app using this key stops working immediately, and stays broken until you create a new key and update the app to use it.`)) return
    router.delete(ap(`/settings/api-keys/${key.id}`))
  }

  return (
    <AccountLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">API Keys</h1>
            <p className="text-sm text-base-content/60 mt-1">
              Create scoped keys for integrations. Aggregate keys read rollups; admin keys read individual responses on Named surveys.
            </p>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => newDialog.current?.showModal()}
            disabled={!can_create}
            title={can_create ? undefined : 'Reactivate your plan to create API keys'}
          >
            <span className="iconify lucide--plus size-4" />
            Create key
          </button>
        </div>

        {!can_create && (
          <div role="alert" className="alert alert-warning alert-soft">
            <span className="iconify lucide--info size-5" />
            <div className="text-sm">
              <p className="font-medium">Your plan is inactive — you can't create new API keys.</p>
              <p className="text-base-content/70">
                Existing keys keep working and can still be revoked.{' '}
                <a href={ap('/billing/expired')} className="link link-primary">Reactivate your plan</a> to create new keys.
              </p>
            </div>
          </div>
        )}

        {flash_token && (
          <div role="alert" className="alert alert-soft alert-warning">
            <div className="flex-1">
              <p className="font-semibold text-sm">Copy this key now — you won't see the full token again.</p>
              <code className="block mt-2 p-2 bg-base-100 rounded text-xs font-mono break-all select-all">{flash_token}</code>
            </div>
          </div>
        )}

        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Active keys</h2>

            {api_keys.length === 0 ? (
              <p className="text-sm text-base-content/50">No API keys yet. Create one to start integrating.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Scope</th>
                      <th>Token</th>
                      <th>Rate limit</th>
                      <th>Last used</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {api_keys.map((key) => (
                      <tr key={key.id} className={key.revoked_at ? 'opacity-50' : ''}>
                        <td>
                          <div className="font-medium">{key.name}</div>
                          <div className="text-xs text-base-content/50">created {new Date(key.created_at).toLocaleDateString()}</div>
                        </td>
                        <td>
                          <span className={`badge badge-sm ${key.scope === 'admin' ? 'badge-warning' : 'badge-info'}`}>
                            {key.scope}
                          </span>
                        </td>
                        <td className="font-mono text-xs">{key.masked_token}</td>
                        <td className="text-xs">{key.rate_limit_per_minute}/min</td>
                        <td className="text-xs text-base-content/60">
                          {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'never'}
                        </td>
                        <td>
                          {key.revoked_at ? (
                            <span className="badge badge-soft badge-error badge-sm">Revoked</span>
                          ) : (
                            <span className="badge badge-soft badge-success badge-sm">Active</span>
                          )}
                        </td>
                        <td className="text-end">
                          {!key.revoked_at && (
                            <button
                              className="btn btn-square btn-ghost btn-sm text-error"
                              title="Revoke"
                              onClick={() => handleRevoke(key)}
                            >
                              <span className="iconify lucide--x size-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <p className="text-sm text-base-content/60">
          See the <a href="/docs/api" className="link link-primary">API docs</a> for endpoint reference.
        </p>
      </div>

      {can_create && <NewApiKeyModal dialogRef={newDialog} createPath={ap('/settings/api-keys')} />}
    </AccountLayout>
  )
}

function NewApiKeyModal({ dialogRef, createPath }: { dialogRef: React.RefObject<HTMLDialogElement | null>; createPath: string }) {
  const form = useForm({
    label: '',
    scope: 'aggregate' as 'aggregate' | 'admin',
    rate_limit_per_minute: '',
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    form.post(createPath, {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        dialogRef.current?.close()
      },
    })
  }

  return (
    <dialog ref={dialogRef} className="modal">
      <div className="modal-box max-w-md">
        <h3 className="text-lg font-bold">New API key</h3>
        <p className="text-sm text-base-content/60">The full token is shown once, right after you create it — copy it then.</p>
        <form onSubmit={handleSubmit} className="py-4 space-y-4">
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Label</legend>
            <input
              type="text"
              className="input input-sm w-full"
              placeholder="e.g. Matchwell production"
              value={form.data.label}
              onChange={(e) => form.setData('label', e.target.value)}
              required
              autoFocus
            />
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Scope</legend>
            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${form.data.scope === 'aggregate' ? 'border-primary bg-primary/5' : 'border-base-300'}`}>
                <input
                  type="radio"
                  className="radio radio-sm radio-primary mt-0.5"
                  checked={form.data.scope === 'aggregate'}
                  onChange={() => form.setData('scope', 'aggregate')}
                />
                <div>
                  <span className="font-medium text-sm">Aggregate</span>
                  <p className="text-xs text-base-content/60 mt-0.5">
                    Reads rollups (means, distributions, completion counts) and filter dimensions. No PII access. Safe default for embedded dashboards.
                  </p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${form.data.scope === 'admin' ? 'border-primary bg-primary/5' : 'border-base-300'}`}>
                <input
                  type="radio"
                  className="radio radio-sm radio-primary mt-0.5"
                  checked={form.data.scope === 'admin'}
                  onChange={() => form.setData('scope', 'admin')}
                />
                <div>
                  <span className="font-medium text-sm">Admin</span>
                  <p className="text-xs text-base-content/60 mt-0.5">
                    Aggregate access plus individual responses with respondent identity (Named surveys only). Audited — every read is logged.
                  </p>
                </div>
              </label>
            </div>
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Rate limit (optional)</legend>
            <input
              type="number"
              className="input input-sm w-32"
              placeholder="120"
              value={form.data.rate_limit_per_minute}
              onChange={(e) => form.setData('rate_limit_per_minute', e.target.value)}
            />
            <p className="text-xs text-base-content/60 mt-1">Requests per minute. Defaults to 120.</p>
          </fieldset>

          <div className="modal-action">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => dialogRef.current?.close()}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={form.processing || !form.data.label.trim()}>
              {form.processing && <span className="loading loading-spinner loading-sm" />}
              Create key
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop"><button>close</button></form>
    </dialog>
  )
}
